import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Radio, BatteryLow, Thermometer, Signal, Pencil, X, RefreshCw, AlertTriangle, Trash2, ArrowLeftRight, SlidersHorizontal, Download, ArrowDown, ArrowUp } from 'lucide-react';
import { apiFetch } from '../api/client';
import type { HierarchyFilter } from './TreeView';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Device {
  phantom_code: string;
  machine_code: string;
  point_index: number;
  point_name: string | null;
  db_id: number;
  sensor_name: string | null;
  model: string | null;
  last_data_at: string | null;
  last_measurement_at: string | null;  // actual vibration data — more reliable than last_data_at (gateway heartbeat)
  battery_voltage: number | null;
  signal_strength: number | null;
  sensor_temperature: number | null;
  machine_name: string;
  company_name: string;
  area_name: string;
  db_name: string | null;
  axis_mapping: Record<string, string>;
}

/** Per-device aggregations from GET /setup/device-metrics. Keyed by phantom_code
 *  and joined onto the device list client-side — one request for the whole page,
 *  never per row. */
interface DeviceMetrics {
  drain_state: 'ok' | 'low_confidence' | 'settling' | 'insufficient' | 'no_data';
  drain_mv_per_day: number | null;      // positive = losing charge = worse
  drain_r2: number | null;
  drain_n_days: number;
  drain_span_days: number;
  drain_window_days: number;
  drain_vs_fleet_ratio: number | null;
  drain_vs_fleet_delta: number | null;
  drain_is_outlier: boolean;
  battery_reset_at: string | null;
  battery_resets_90d: number;
  battery_reset_frequent: boolean;
  battery_last_reset_at: string | null;
  scheduled_events: number;
  scheduled_per_day: number;
  scheduled_window_days: number;
  history_days: number | null;
  scheduled_partial_history: boolean;
}

interface FleetMetrics {
  mean_drain_mv_per_day: number | null;
  n_devices: number;
  window_days: number;
  outlier_ratio: number;
  reset_window_days: number;
  reset_frequent_min: number;
  collection_window_days: number;
}

type AxisLabel = 'H' | 'V' | 'A';
type PhysChannel = 'x' | 'y' | 'z';
type DeviceStatus = 'connected' | 'low_battery' | 'disconnected';
type StatusFilter = 'all' | 'connected' | 'low_battery' | 'disconnected';

/** 'default' keeps the server's hierarchy ordering (db → company → area →
 *  machine → point). The rest sort on the derived metrics. */
type SortField = 'default' | 'lastseen' | 'drain' | 'resets' | 'scheduled';
type SortDir   = 'desc' | 'asc';

const SORT_LABEL: Record<SortField, string> = {
  default:   'Hierarchy',
  lastseen:  'Last seen',
  drain:     'Battery drain',
  resets:    'Battery resets',
  scheduled: 'Scheduled collections',
};

/** What "descending" means per field, spelled out — for drain, bigger is worse,
 *  so desc = worst first, and that should not have to be inferred from an arrow.
 *
 *  Last seen is deliberately the odd one out: desc = NEWEST first, which is the
 *  least-concerning end, not the worst. Chosen explicitly; flip to asc for a
 *  longest-dark-first triage order. */
const SORT_DIR_HINT: Record<Exclude<SortField, 'default'>, { desc: string; asc: string }> = {
  lastseen:  { desc: 'newest first',         asc: 'oldest first' },
  drain:     { desc: 'fastest drain first',  asc: 'slowest drain first' },
  resets:    { desc: 'most resets first',    asc: 'fewest resets first' },
  scheduled: { desc: 'most frequent first',  asc: 'least frequent first' },
};

interface ReviewDevicesProps {
  hierarchyFilter: HierarchyFilter | null;
  onClearFilter: () => void;
  /** Open the Point page for the point this sensor is mounted on. Resolved by
   *  (machine_code, point_index) — devices.point_name disagrees with axes.point_name
   *  on a handful of rows, so the index is the identity and the name is only a label. */
  onPointClick?: (machineCode: string, machineName: string, pointName: string | null, pointIndex: number) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseLastSeen(raw: string | null): Date | null {
  if (!raw) return null;
  const d = new Date(raw.replace(/\//g, '-'));
  // An unparseable stamp yields an Invalid Date, which is TRUTHY. Returned as-is
  // it made deviceStatus() compute daysSince = NaN, and `NaN > 5` is false — so a
  // malformed timestamp classified the sensor as CONNECTED. On a page whose job
  // is finding dark sensors, an unreadable timestamp must fail toward
  // "disconnected", never toward "healthy". null makes the caller's Infinity
  // branch fire instead.
  return isNaN(d.getTime()) ? null : d;
}

/** Sortable integer for a 'YYYY/MM/DD HH:MM:SS' (or dashed) timestamp: digits
 *  only, zero-padded to a fixed width, so numeric order IS chronological order.
 *
 *  Deliberately does NOT go through `new Date()`. Two reasons, both measured:
 *  (1) `devices.last_data_at` holds a MIX of Eastern and UTC values — 79 devices
 *      differ from their own last_measurement_at by exactly the 4h offset (the
 *      Eastern/UTC split mqtt_subscriber.py:151 documents). Date parsing would
 *      silently reinterpret those against the browser's zone.
 *  (2) `new Date('garbage')` returns an Invalid Date, which is TRUTHY — the same
 *      shape that makes deviceStatus() read a malformed stamp as 'connected'.
 *      Returning null here keeps a bad value out of the ordering instead.
 *  Nothing currently falls back to last_data_at (588/589 devices carry a clean
 *  last_measurement_at), but the sort must not be the thing that depends on that. */
function timestampKey(raw: string | null): number | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 8) return null;                 // not even a whole date
  const n = Number(digits.slice(0, 14).padEnd(14, '0'));  // YYYYMMDDHHMMSS
  return Number.isFinite(n) ? n : null;
}

function realBattery(v: number | null): number | null {
  return v !== null && v > 0 ? v : null;
}
function realTemp(c: number | null): number | null {
  return c !== null && c > -100 ? c : null;
}
function realSignal(s: number | null): number | null {
  return s !== null && s !== 0 ? s : null;
}

function deviceStatus(d: Device): DeviceStatus {
  // Use last_measurement_at (actual vibration data) for connectivity — last_data_at is a gateway
  // heartbeat that updates even when a sensor is RF-disconnected from the measurement point.
  const effectiveLastSeen = d.last_measurement_at || d.last_data_at;
  const lastSeen = parseLastSeen(effectiveLastSeen);
  const daysSince = lastSeen ? (Date.now() - lastSeen.getTime()) / 86_400_000 : Infinity;
  if (daysSince > 5) return 'disconnected';
  const batt = realBattery(d.battery_voltage);
  if (batt !== null && batt < 2.6) return 'low_battery';
  return 'connected';
}

function fmtLastSeen(raw: string | null): { date: string; time: string } | null {
  if (!raw) return null;
  const d = parseLastSeen(raw);
  if (!d || isNaN(d.getTime())) return null;
  return { date: d.toLocaleDateString('en-CA'), time: d.toTimeString().slice(0, 8) };
}

function fmtTemp(c: number | null): string {
  const v = realTemp(c);
  return v === null ? '—' : `${((v * 9) / 5 + 32).toFixed(0)} °F`;
}

function fmtBattery(v: number | null): string {
  const b = realBattery(v);
  return b === null ? '—' : `${b.toFixed(2)} V`;
}

// ---------------------------------------------------------------------------
// CSV export — pure client-side. Serialises the already-loaded list exactly as
// the page currently shows it: the caller passes the filtered/searched/sorted
// array, and the columns below are the card's fields in display order.
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<DeviceStatus, string> = {
  connected: 'Connected',
  low_battery: 'Low battery',
  disconnected: 'Disconnected',
};

/** RFC 4180 quoting: wrap when the value holds a comma, quote, CR/LF, or edge
 *  whitespace; double any embedded quote. */
function csvCell(value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value);
  if (s === '') return '';
  return /[",\r\n]/.test(s) || s !== s.trim() ? `"${s.replace(/"/g, '""')}"` : s;
}

/** "YYYY-MM-DD HH:MM:SS" — the card shows date and time stacked; one cell here. */
function csvTimestamp(raw: string | null): string {
  const f = fmtLastSeen(raw);
  return f ? `${f.date} ${f.time}` : '';
}

const DRAIN_STATE_LABEL: Record<DeviceMetrics['drain_state'], string> = {
  ok:             'Measured',
  settling:       'Settling after battery change',
  low_confidence: 'Low confidence',
  insufficient:   'Insufficient data',
  no_data:        'No battery telemetry',
};

/** Units live in the headers so the cells stay numeric for spreadsheets.
 *  Column order mirrors the card: identity, connectivity, battery (incl. the
 *  derived drain block), collection cadence, then the hierarchy breadcrumb. */
const CSV_COLUMNS: {
  header: string;
  value: (d: Device, m?: DeviceMetrics) => unknown;
}[] = [
  { header: 'Status',            value: d => STATUS_LABEL[deviceStatus(d)] },
  { header: 'Device',            value: d => d.phantom_code },
  { header: 'Last Data',         value: d => csvTimestamp(d.last_measurement_at || d.last_data_at) },
  { header: 'Gateway Last Seen', value: d => csvTimestamp(d.last_data_at) },
  { header: 'Signal (dB)',       value: d => realSignal(d.signal_strength) ?? '' },
  { header: 'Temperature (°F)',  value: d => {
      const c = realTemp(d.sensor_temperature);
      return c === null ? '' : ((c * 9) / 5 + 32).toFixed(0);
    } },
  { header: 'Battery (V)',       value: d => {
      const b = realBattery(d.battery_voltage);
      return b === null ? '' : b.toFixed(2);
    } },
  // Positive = losing charge. Blank when no rate was computed; the state column
  // says which flavour of "no rate" it was, so a blank is never ambiguous.
  { header: 'Battery Drain (mV/day)', value: (_d, m) =>
      m && m.drain_mv_per_day !== null ? m.drain_mv_per_day.toFixed(2) : '' },
  { header: 'Drain Confidence',       value: (_d, m) => m ? DRAIN_STATE_LABEL[m.drain_state] : '' },
  { header: 'Drain vs Fleet (x)',     value: (_d, m) =>
      m && m.drain_vs_fleet_ratio !== null ? m.drain_vs_fleet_ratio.toFixed(2) : '' },
  { header: 'Drain Outlier',          value: (_d, m) => (m?.drain_is_outlier ? 'YES' : '') },
  { header: 'Battery Resets (90d)',   value: (_d, m) => (m ? m.battery_resets_90d : '') },
  { header: 'Scheduled/day (10d)',    value: (_d, m) => (m ? m.scheduled_per_day.toFixed(2) : '') },
  { header: 'Scheduled Events (10d)', value: (_d, m) => (m ? m.scheduled_events : '') },
  // Guards against reading a low per-day figure as "quiet" on a new install.
  { header: 'Partial History',        value: (_d, m) => (m?.scheduled_partial_history ? 'YES' : '') },
  { header: 'Model',             value: d => d.model ?? '' },
  { header: 'Name',              value: d => d.sensor_name ?? '' },
  { header: 'Axis Mapping',      value: d => {
      const m = d.axis_mapping ?? {};
      if (Object.keys(m).length !== 3) return '';
      return (['x', 'y', 'z'] as PhysChannel[])
        .map(ch => `${ch.toUpperCase()}→${(m as any)[ch]}`)
        .join(' · ');
    } },
  { header: 'Database',          value: d => d.db_name ?? '' },
  { header: 'Company',           value: d => d.company_name ?? '' },
  { header: 'Area',              value: d => d.area_name ?? '' },
  { header: 'Machine',           value: d => d.machine_name ?? '' },
  { header: 'Point',             value: d => d.point_name ?? '' },
];

/** Sort key for one device. null = no value for this field, which the comparator
 *  parks at the end rather than treating as zero.
 *
 *  Drain deliberately includes `settling` and `low_confidence` fits. Those are
 *  excluded from the fleet mean and from outlier flagging because a short
 *  post-replacement transient is not comparable to a steady-state rate — but
 *  sorting is exploration, not an assertion, and each card carries its own badge
 *  explaining the basis. Dropping them here would re-hide exactly the
 *  just-serviced sensors worth looking at. Use the "Battery resets" sort for the
 *  replacement-frequency question, which needs no fit at all. */
function sortValue(
  d: Device,
  metrics: Record<string, DeviceMetrics>,
  field: SortField,
): number | null {
  // Last seen comes off the device row, not the metrics payload — so it works
  // even when /setup/device-metrics is unavailable. Same coalesce the card
  // displays and deviceStatus() classifies on, so the order always matches the
  // date shown on the card.
  if (field === 'lastseen') return timestampKey(d.last_measurement_at || d.last_data_at);
  const m = metrics[d.phantom_code];
  if (!m) return null;
  if (field === 'drain')     return m.drain_mv_per_day;      // null when no fit
  if (field === 'resets')    return m.battery_resets_90d;
  if (field === 'scheduled') return m.scheduled_per_day;
  return null;
}

function exportDevicesCsv(devices: Device[], metrics: Record<string, DeviceMetrics>) {
  const lines = [
    CSV_COLUMNS.map(c => csvCell(c.header)).join(','),
    ...devices.map(d =>
      CSV_COLUMNS.map(c => csvCell(c.value(d, metrics[d.phantom_code]))).join(',')),
  ];
  // Leading BOM so Excel reads the degree sign / arrows as UTF-8, CRLF per RFC 4180.
  const blob = new Blob(['﻿' + lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const now = new Date();
  const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = `review-devices-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Status dot
// ---------------------------------------------------------------------------

function StatusDot({ status }: { status: DeviceStatus }) {
  const colour =
    status === 'connected'   ? 'bg-green-500' :
    status === 'low_battery' ? 'bg-orange-400' : 'bg-red-500';
  return <span className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${colour} shadow-sm`} />;
}

function BatteryIcon({ v }: { v: number | null }) {
  const b = realBattery(v);
  if (b === null) return null;
  const cls = b < 2.2 ? 'text-red-500' : b < 2.6 ? 'text-orange-400' : 'text-gray-400';
  return <BatteryLow className={`w-5 h-5 ${cls}`} />;
}

// ---------------------------------------------------------------------------
// Axis mapping modal
// ---------------------------------------------------------------------------

const AXIS_LABELS: AxisLabel[] = ['H', 'V', 'A'];
const CHANNEL_LABELS: Record<PhysChannel, string> = { x: 'X', y: 'Y', z: 'Z' };

function AxisMappingModal({
  device,
  onClose,
  onSaved,
}: {
  device: Device;
  onClose: () => void;
  onSaved: (phantomCode: string, mapping: Record<string, string>) => void;
}) {
  const initial = (
    device.axis_mapping && Object.keys(device.axis_mapping).length === 3
      ? device.axis_mapping
      : { x: 'H', y: 'V', z: 'A' }
  ) as Record<PhysChannel, AxisLabel>;

  const [mapping, setMapping] = useState<Record<PhysChannel, AxisLabel>>(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setChannel = (ch: PhysChannel, axis: AxisLabel) => {
    setMapping(prev => {
      const current = prev[ch];
      const swapCh = (Object.entries(prev) as [PhysChannel, AxisLabel][]).find(([k, v]) => v === axis && k !== ch)?.[0];
      const next = { ...prev, [ch]: axis } as Record<PhysChannel, AxisLabel>;
      if (swapCh) next[swapCh] = current;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      await apiFetch(`/setup/devices/${device.phantom_code}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ axis_mapping: mapping }),
      });
      onSaved(device.phantom_code, mapping);
      onClose();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="text-base font-semibold text-gray-800">Axis Mapping</h2>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{device.phantom_code}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-5">Map each physical sensor channel to its measurement axis.</p>

        <div className="space-y-3 mb-6">
          {(['x', 'y', 'z'] as PhysChannel[]).map(ch => (
            <div key={ch} className="flex items-center gap-3">
              <span className="w-6 text-sm font-semibold text-gray-600">{CHANNEL_LABELS[ch]}</span>
              <div className="flex gap-1">
                {AXIS_LABELS.map(axis => {
                  const active = mapping[ch] === axis;
                  return (
                    <button key={axis} type="button" onClick={() => setChannel(ch, axis)}
                      className={`w-10 py-1.5 rounded text-sm font-semibold border transition-colors ${
                        active
                          ? 'bg-[#00D4BE] border-[#00D4BE] text-white'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-[#00D4BE]/50'
                      }`}>
                      {axis}
                    </button>
                  );
                })}
              </div>
              <span className="text-xs text-gray-400 ml-1">→ {mapping[ch]}</span>
            </div>
          ))}
        </div>

        {err && <p className="text-xs text-red-500 mb-3">{err}</p>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm font-semibold bg-[#00D4BE] text-white rounded-lg hover:bg-[#00bfab] transition-colors disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Change sensor modal — field swap: register a new sensor on this point and
// retire the old one (POST /setup/devices/{code}/replace).
// ---------------------------------------------------------------------------

function ChangeSensorModal({
  device,
  onClose,
  onReplaced,
}: {
  device: Device;
  onClose: () => void;
  onReplaced: () => void;
}) {
  const [newCode, setNewCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const trimmed = newCode.trim();
  const valid = /^\d{6,}$/.test(trimmed) && trimmed !== device.phantom_code;

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      await apiFetch(`/setup/devices/${device.phantom_code}/replace`, {
        method: 'POST',
        body: JSON.stringify({ new_phantom_code: trimmed }),
      });
      onReplaced();
      onClose();
    } catch (e: any) {
      setErr(e.message ?? 'Change failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-base font-semibold text-gray-800">Change Sensor</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Register a new sensor on this point and retire the current one. The axis mapping is carried
          over and vibration history (keyed to the point, not the sensor) is unaffected.
        </p>

        <div className="text-xs text-gray-500 mb-4 space-y-1">
          <div><span className="text-gray-400 uppercase tracking-wide mr-1">Current</span>
            <span className="font-mono text-gray-700">{device.phantom_code}</span></div>
          <div><span className="text-gray-400 uppercase tracking-wide mr-1">Point</span>
            <span className="text-gray-700">{[device.machine_name, device.point_name].filter(Boolean).join(' › ')}</span></div>
        </div>

        <label className="block text-xs font-medium text-gray-600 mb-1">New sensor phantom code</label>
        <input type="text" inputMode="numeric" value={newCode} autoFocus
          onChange={e => setNewCode(e.target.value.replace(/[^\d]/g, ''))}
          placeholder="e.g. 189322882"
          className="w-full px-3 py-2 text-sm font-mono bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D4BE]/60 transition-colors" />
        {trimmed && trimmed === device.phantom_code && (
          <p className="text-xs text-amber-600 mt-1">That is the current sensor.</p>
        )}

        {err && <p className="text-xs text-red-500 mb-3 mt-2">{err}</p>}
        <div className="flex gap-2 justify-end mt-5">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={!valid || saving}
            className="px-4 py-2 text-sm font-semibold bg-[#00D4BE] text-white rounded-lg hover:bg-[#00bfab] transition-colors disabled:opacity-50">
            {saving ? 'Changing…' : 'Change sensor'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete sensor confirmation (DELETE /setup/devices/{code})
// ---------------------------------------------------------------------------

function DeleteSensorModal({
  device,
  onClose,
  onDeleted,
}: {
  device: Device;
  onClose: () => void;
  onDeleted: (code: string) => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState('');

  const handleDelete = async () => {
    setDeleting(true); setErr('');
    try {
      await apiFetch(`/setup/devices/${device.phantom_code}`, { method: 'DELETE' });
      onDeleted(device.phantom_code);
      onClose();
    } catch (e: any) {
      setErr(e.message ?? 'Delete failed');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-4 h-4 text-red-500" />
          </div>
          <h2 className="text-base font-semibold text-gray-800">Delete Sensor</h2>
        </div>
        <p className="text-sm text-gray-600 mb-1">
          Remove sensor <span className="font-mono text-gray-800">{device.phantom_code}</span> from this point?
        </p>
        <p className="text-xs text-gray-400 mb-2">{[device.machine_name, device.point_name].filter(Boolean).join(' › ')}</p>
        <p className="text-xs text-gray-500 mb-5">
          This removes only the sensor assignment. Vibration history and alarms (keyed to the point,
          not the sensor) are unaffected.
        </p>

        {err && <p className="text-xs text-red-500 mb-3">{err}</p>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleDelete} disabled={deleting}
            className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60">
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Derived-metrics strip — battery drain vs fleet, reset frequency, cadence
// ---------------------------------------------------------------------------

function MetricsStrip({ m, fleet }: { m?: DeviceMetrics; fleet: FleetMetrics | null }) {
  if (!m) return null;

  const rate = m.drain_mv_per_day;
  const hasRate = rate !== null;
  // Faster drain is worse, always. Colour tracks severity, never direction of
  // the number alone: a negative rate means the pack gained voltage.
  const rateColour =
    m.drain_is_outlier            ? 'text-red-600 font-semibold' :
    m.drain_state === 'settling'  ? 'text-gray-500' :
    m.drain_state === 'low_confidence' ? 'text-gray-400' :
    'text-gray-600';

  const stateNote =
    m.drain_state === 'settling'
      ? `settling — measured over ${m.drain_span_days}d since the battery change, not a steady rate`
      : m.drain_state === 'low_confidence'
      ? `low confidence — fit explains little of the movement (r²=${m.drain_r2 ?? '—'})`
      : m.drain_state === 'insufficient'
      ? `insufficient — ${m.drain_n_days} day(s) of battery telemetry in the window`
      : m.drain_state === 'no_data'
      ? 'no battery telemetry in the window'
      : `OLS over ${m.drain_n_days} daily medians spanning ${m.drain_span_days}d (r²=${m.drain_r2 ?? '—'})`;

  return (
    <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm text-gray-500 mb-3">
      {/* battery drain + comparison to the single shared fleet mean */}
      <span className="flex items-center gap-1.5" title={stateNote}>
        <span className="text-gray-400 text-xs uppercase tracking-wide">Drain</span>
        {hasRate ? (
          <>
            <span className={rateColour}>
              {rate! > 0 ? '↓' : rate! < 0 ? '↑' : ''} {Math.abs(rate!).toFixed(1)} mV/day
            </span>
            {m.drain_vs_fleet_ratio !== null && (
              <span className={m.drain_is_outlier ? 'text-red-500 text-xs' : 'text-gray-400 text-xs'}>
                {m.drain_vs_fleet_ratio.toFixed(1)}× fleet
              </span>
            )}
            {m.drain_state === 'settling' && (
              <span className="text-[10px] uppercase tracking-wide text-amber-600 border border-amber-200 bg-amber-50 rounded px-1 py-0.5">
                settling
              </span>
            )}
            {m.drain_state === 'low_confidence' && (
              <span className="text-[10px] uppercase tracking-wide text-gray-400 border border-gray-200 rounded px-1 py-0.5">
                low conf
              </span>
            )}
          </>
        ) : (
          <span className="text-gray-300">—</span>
        )}
        {m.drain_is_outlier && (
          <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
        )}
      </span>

      {/* replacement frequency — the direct "changed more often than others" signal */}
      {m.battery_resets_90d > 0 && (
        <span className="flex items-center gap-1.5"
          title={`${m.battery_resets_90d} battery replacement(s) or voltage reset(s) detected in the last ${fleet?.reset_window_days ?? 90} days`}>
          <span className="text-gray-400 text-xs uppercase tracking-wide">Resets</span>
          <span className={m.battery_reset_frequent ? 'text-red-600 font-semibold' : 'text-gray-600'}>
            {m.battery_resets_90d} in {fleet?.reset_window_days ?? 90}d
          </span>
          {m.battery_reset_frequent && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
        </span>
      )}

      {/* scheduled (reason=2) collection cadence */}
      <span className="flex items-center gap-1.5"
        title={`${m.scheduled_events} scheduled collection event(s) in the last ${m.scheduled_window_days} days`
          + (m.scheduled_partial_history
              ? ` — only ${m.history_days}d of history, so the per-day figure is understated`
              : '')}>
        <span className="text-gray-400 text-xs uppercase tracking-wide">Scheduled</span>
        <span className={m.scheduled_events === 0 ? 'text-gray-300' : 'text-gray-600'}>
          {m.scheduled_per_day.toFixed(1)}/day
        </span>
        {m.scheduled_partial_history && (
          <span className="text-[10px] uppercase tracking-wide text-amber-600 border border-amber-200 bg-amber-50 rounded px-1 py-0.5">
            partial history
          </span>
        )}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Device card
// ---------------------------------------------------------------------------

function DeviceCard({
  device,
  onEditAxis,
  onChangeSensor,
  onDelete,
  onOpenPoint,
  metrics,
  fleet,
}: {
  device: Device;
  onEditAxis: () => void;
  onChangeSensor: () => void;
  onDelete: () => void;
  onOpenPoint?: () => void;
  metrics?: DeviceMetrics;
  fleet: FleetMetrics | null;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  // Fixed positioning (computed from the trigger rect) so the menu escapes the
  // card's `overflow-hidden` clip instead of being cut off inside it.
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setMenuPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    setMenuOpen(true);
  };
  const status = deviceStatus(device);
  // Show actual measurement time; fall back to gateway timestamp only when no measurements exist
  const lastSeen = fmtLastSeen(device.last_measurement_at || device.last_data_at);
  // Secondary row for the stale-sensor case: something is still making contact
  // for this device while its measurements have stopped. That is only worth
  // saying when the contact stamp is genuinely NEWER than the last measurement —
  // which is what this line was always documented to mean but never checked, so
  // it also rendered when last_data_at was older and the comparison said nothing.
  //
  // Compared via timestampKey, not Date: same digits-only key the sort uses, so
  // the two can never disagree about which value is more recent.
  const contactKey = timestampKey(device.last_data_at);
  const measKey    = timestampKey(device.last_measurement_at);
  const gwSeen = fmtLastSeen(device.last_data_at);
  const showGw = status === 'disconnected' && gwSeen !== null
    && contactKey !== null && measKey !== null && contactKey > measKey;
  const borderColour =
    status === 'connected'   ? 'border-gray-200' :
    status === 'low_battery' ? 'border-orange-200' : 'border-red-200';

  return (
    <div className={`bg-white rounded-xl border ${borderColour} shadow-sm mb-3 overflow-hidden`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 sm:px-5 py-3 border-b border-gray-100">
        <StatusDot status={status} />
        <Radio className="w-4 h-4 text-gray-400 flex-shrink-0" />
        {onOpenPoint ? (
          <button type="button" onClick={onOpenPoint}
            title={`Open point page — ${[device.machine_name, device.point_name].filter(Boolean).join(' › ')}`}
            className="font-bold text-base tracking-wide text-[#00D4BE] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00D4BE]/50 rounded">
            {device.phantom_code}
          </button>
        ) : (
          <span className="font-bold text-gray-800 text-base tracking-wide">{device.phantom_code}</span>
        )}

        <div className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-1 sm:gap-5 text-sm text-gray-500">
          {lastSeen && (
            <div className="text-right">
              <div className={`text-xs uppercase tracking-wide font-medium ${status === 'disconnected' ? 'text-red-400' : 'text-gray-400'}`}>
                Last data
              </div>
              <div className={`text-xs ${status === 'disconnected' ? 'text-red-500 font-medium' : 'text-gray-500'}`}>{lastSeen.date}</div>
              <div className={`text-xs font-mono ${status === 'disconnected' ? 'text-red-500' : 'text-gray-500'}`}>{lastSeen.time}</div>
              {/* "Last contact", not "GW seen": last_data_at is written by the
                  gateway paths AND by the API poller's poll_sensors(), and 283
                  of the 285 poller-written rows belong to devices with no
                  gateway at all. Naming it a gateway sighting asserts a
                  delivery path those devices do not have. */}
              {showGw && gwSeen && (
                <div className="text-[10px] text-gray-400 mt-0.5"
                  title="Last contact recorded for this sensor (gateway heartbeat or API poll) — newer than its last actual measurement">
                  <span className="text-gray-300">Last contact </span>{gwSeen.date}
                </div>
              )}
            </div>
          )}
          {realSignal(device.signal_strength) !== null && (
            <div className="flex items-center gap-1">
              <Signal className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium">{device.signal_strength} dB</span>
            </div>
          )}
          {realTemp(device.sensor_temperature) !== null && (
            <div className="flex items-center gap-1">
              <Thermometer className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium">{fmtTemp(device.sensor_temperature)}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <BatteryIcon v={device.battery_voltage} />
            <span className={`text-xs font-medium ${
              realBattery(device.battery_voltage) !== null && realBattery(device.battery_voltage)! < 2.2 ? 'text-red-500' :
              realBattery(device.battery_voltage) !== null && realBattery(device.battery_voltage)! < 2.6 ? 'text-orange-500' :
              'text-gray-500'
            }`}>
              {fmtBattery(device.battery_voltage)}
            </span>
          </div>
          <div>
            <button ref={btnRef} type="button" onClick={() => (menuOpen ? setMenuOpen(false) : openMenu())}
              title="Sensor actions" aria-haspopup="menu" aria-expanded={menuOpen}
              className="p-1.5 rounded-lg text-gray-400 hover:text-[#00D4BE] hover:bg-[#00D4BE]/10 transition-colors">
              <Pencil className="w-4 h-4" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div role="menu"
                  className="fixed z-50 w-52 bg-white border border-gray-200 rounded-lg shadow-xl py-1 text-sm"
                  style={{ top: menuPos.top, right: menuPos.right }}>
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onEditAxis(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-gray-50 text-left">
                    <SlidersHorizontal className="w-4 h-4 text-gray-400" /> Edit axis mapping
                  </button>
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onChangeSensor(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-gray-700 hover:bg-gray-50 text-left">
                    <ArrowLeftRight className="w-4 h-4 text-gray-400" /> Change sensor
                  </button>
                  <div className="my-1 border-t border-gray-100" />
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onDelete(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-red-600 hover:bg-red-50 text-left">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-5 py-3">
        <MetricsStrip m={metrics} fleet={fleet} />
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-500 mb-3">
          <span><span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Model</span>{device.model ?? '—'}</span>
          <span><span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Name</span>{device.sensor_name ?? '—'}</span>
          {Object.keys(device.axis_mapping ?? {}).length === 3 && (
            <span className="flex items-center gap-1">
              <span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Axes</span>
              {(['x', 'y', 'z'] as PhysChannel[]).map((ch, i) => (
                <span key={ch} className="text-xs font-mono">
                  {i > 0 && <span className="text-gray-300 mx-0.5">·</span>}
                  <span className="text-gray-400">{ch.toUpperCase()}</span>
                  <span className="text-gray-300">→</span>
                  <span className="text-[#00D4BE] font-semibold">{(device.axis_mapping as any)[ch]}</span>
                </span>
              ))}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-500 flex-wrap">
          {[device.db_name, device.company_name, device.area_name, device.machine_name, device.point_name]
            .filter(Boolean).map((part, i, arr) => (
              <span key={i} className="flex items-center gap-1">
                <span className="text-gray-600">{part}</span>
                {i < arr.length - 1 && <span className="text-gray-300">›</span>}
              </span>
            ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status summary bar
// ---------------------------------------------------------------------------

function StatusBar({
  devices,
  statusFilter,
  onStatusFilter,
}: {
  devices: Device[];
  statusFilter: StatusFilter;
  onStatusFilter: (s: StatusFilter) => void;
}) {
  const counts = {
    connected:    devices.filter(d => deviceStatus(d) === 'connected').length,
    low_battery:  devices.filter(d => deviceStatus(d) === 'low_battery').length,
    disconnected: devices.filter(d => deviceStatus(d) === 'disconnected').length,
  };

  const pill = (s: StatusFilter, count: number, dot: string, label: string) => {
    const active = statusFilter === s;
    return (
      <button type="button" onClick={() => onStatusFilter(active ? 'all' : s)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
          active
            ? 'border-transparent shadow-sm ' + (
                s === 'connected'    ? 'bg-green-100 text-green-700' :
                s === 'low_battery' ? 'bg-orange-100 text-orange-700' :
                                      'bg-red-100 text-red-700')
            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
        }`}>
        <span className={`w-2.5 h-2.5 rounded-full inline-block flex-shrink-0 ${dot}`} />
        {count} {label}
      </button>
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm mb-4">
      {pill('connected',    counts.connected,    'bg-green-500',  'Connected')}
      {pill('low_battery',  counts.low_battery,  'bg-orange-400', 'Low battery')}
      {pill('disconnected', counts.disconnected, 'bg-red-500',    'Disconnected')}
      <span className="text-gray-400 ml-auto text-xs">Total: {devices.length}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ReviewDevices({ hierarchyFilter, onClearFilter, onPointClick }: ReviewDevicesProps) {
  const [allDevices, setAllDevices]     = useState<Device[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField]       = useState<SortField>('default');
  const [sortDir, setSortDir]           = useState<SortDir>('desc');
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [changingSensor, setChangingSensor] = useState<Device | null>(null);
  const [deletingDevice, setDeletingDevice] = useState<Device | null>(null);
  const [lastUpdated, setLastUpdated]   = useState<Date | null>(null);
  const [refreshing, setRefreshing]     = useState(false);
  const [refreshStale, setRefreshStale] = useState(false);
  const [metrics, setMetrics]           = useState<Record<string, DeviceMetrics>>({});
  const [fleet, setFleet]               = useState<FleetMetrics | null>(null);
  const [metricsFailed, setMetricsFailed] = useState(false);
  // The metric sorts are meaningless until the payload lands, so the control is
  // held disabled rather than silently sorting everything into one flat tie.
  const metricsLoaded = Object.keys(metrics).length > 0;

  // Derived metrics come from a separate endpoint, fetched on mount and on
  // manual refresh only — NOT on the 60 s poll below. They aggregate ~110k
  // health-log rows and ~226k reason=2 rows server-side; running that every
  // minute would be pure waste for numbers built on 10- and 30-day windows.
  const loadMetrics = useCallback((force = false) => {
    apiFetch(`/setup/device-metrics${force ? '?refresh=true' : ''}`)
      .then((r: { fleet: FleetMetrics; devices: Record<string, DeviceMetrics> }) => {
        setMetrics(r.devices ?? {});
        setFleet(r.fleet ?? null);
        setMetricsFailed(false);
      })
      // Soft-fail: the device list is the page's primary content and must still
      // render. Missing metrics degrade to '—', they do not blank the page.
      .catch(() => setMetricsFailed(true));
  }, []);

  const loadAll = useCallback((showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    else setLoading(true);
    apiFetch('/setup/devices')
      .then((devs: Device[]) => {
        setAllDevices(devs);
        setLastUpdated(new Date());
        setError('');
        setRefreshStale(false);
      })
      .catch(() => setError('Failed to load devices.'))
      .finally(() => { setLoading(false); setRefreshing(false); });
    loadMetrics(showSpinner);
  }, [loadMetrics]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const id = setInterval(() => {
      apiFetch('/setup/devices').then((devs: Device[]) => {
        // Success: adopt fresh data, advance the "Updated" clock, clear any stale flag.
        setAllDevices(devs); setLastUpdated(new Date()); setRefreshStale(false);
      }).catch(() => {
        // W4: a background refresh failure must NOT advance "Updated" (that would
        // make stale data look fresh). Keep the last-good list, flag it as stale.
        setRefreshStale(true);
      });
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  // Selecting "Disconnected" implies you want them ordered by when they went
  // dark, so last-seen is applied automatically — but only while the dropdown is
  // still on Hierarchy, exactly as the low-battery/voltage precedent works. An
  // explicit choice always wins.
  const sortIsAutomatic  = sortField === 'default' && statusFilter === 'disconnected';
  const effectiveSortField: SortField = sortIsAutomatic ? 'lastseen' : sortField;
  const effectiveSortDir:   SortDir   = sortIsAutomatic ? 'desc' : sortDir;

  const filtered = useMemo(() => {
    let list = allDevices;

    if (hierarchyFilter) {
      list = list.filter(d => d.db_name === hierarchyFilter.dbName);
      if (hierarchyFilter.companyName) list = list.filter(d => d.company_name === hierarchyFilter.companyName);
      if (hierarchyFilter.areaName)    list = list.filter(d => d.area_name    === hierarchyFilter.areaName);
    }
    if (statusFilter !== 'all') {
      list = list.filter(d => deviceStatus(d) === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        d.phantom_code.includes(q) ||
        (d.sensor_name ?? '').toLowerCase().includes(q) ||
        d.machine_name.toLowerCase().includes(q) ||
        (d.point_name ?? '').toLowerCase().includes(q)
      );
    }
    // The implicit low-battery ordering only applies while no explicit sort is
    // chosen — an explicit choice must win over it.
    if (sortField === 'default' && statusFilter === 'low_battery') {
      list = [...list].sort((a, b) => (a.battery_voltage ?? 99) - (b.battery_voltage ?? 99));
    } else if (effectiveSortField !== 'default') {
      list = [...list].sort((a, b) => {
        const va = sortValue(a, metrics, effectiveSortField);
        const vb = sortValue(b, metrics, effectiveSortField);
        // A device with no value sorts LAST in both directions. Coercing a
        // missing rate to 0 would rank "we can't measure this" alongside
        // "measured, and it's fine" — the two are not the same claim.
        if (va === null && vb === null) return 0;
        if (va === null) return 1;
        if (vb === null) return -1;
        return effectiveSortDir === 'desc' ? vb - va : va - vb;
      });
    }
    return list;
  }, [allDevices, hierarchyFilter, statusFilter, search, effectiveSortField, effectiveSortDir, metrics]);

  // How many listed devices have no value for the active sort — they are parked
  // at the end, and saying so stops that tail reading as "these are the lowest"
  // (or, for last seen, "these are the most recent").
  const unsortableCount = useMemo(() => {
    if (effectiveSortField === 'default') return 0;
    return filtered.filter(d => sortValue(d, metrics, effectiveSortField) === null).length;
  }, [filtered, metrics, effectiveSortField]);

  const visibleForStatusBar = useMemo(() => {
    let list = allDevices;
    if (hierarchyFilter) {
      list = list.filter(d => d.db_name === hierarchyFilter.dbName);
      if (hierarchyFilter.companyName) list = list.filter(d => d.company_name === hierarchyFilter.companyName);
      if (hierarchyFilter.areaName)    list = list.filter(d => d.area_name    === hierarchyFilter.areaName);
    }
    return list;
  }, [allDevices, hierarchyFilter]);

  const handleAxisMappingSaved = (phantomCode: string, mapping: Record<string, string>) => {
    setAllDevices(prev => prev.map(d => d.phantom_code === phantomCode ? { ...d, axis_mapping: mapping } : d));
  };

  // After a sensor swap the phantom_code changed and the new sensor's metadata
  // (battery/signal/name) differs — refetch rather than guess the new row.
  const handleReplaced = () => loadAll(true);

  const handleDeleted = (phantomCode: string) => {
    setAllDevices(prev => prev.filter(d => d.phantom_code !== phantomCode));
  };

  return (
    <>
      {editingDevice && (
        <AxisMappingModal
          device={editingDevice}
          onClose={() => setEditingDevice(null)}
          onSaved={handleAxisMappingSaved}
        />
      )}
      {changingSensor && (
        <ChangeSensorModal
          device={changingSensor}
          onClose={() => setChangingSensor(null)}
          onReplaced={handleReplaced}
        />
      )}
      {deletingDevice && (
        <DeleteSensorModal
          device={deletingDevice}
          onClose={() => setDeletingDevice(null)}
          onDeleted={handleDeleted}
        />
      )}

      <div className="flex flex-col h-full min-h-0">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-800">
              {hierarchyFilter ? hierarchyFilter.label : 'All Devices'}
            </h1>
            {hierarchyFilter && (
              <button type="button" onClick={onClearFilter}
                className="text-xs text-[#00D4BE] hover:underline mt-0.5">
                ← All devices
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {lastUpdated && (
              <span className={`text-xs ${refreshStale ? 'text-amber-600' : 'text-gray-400'}`}>
                Updated {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                {refreshStale && ' (stale)'}
              </span>
            )}
            <button type="button" onClick={() => loadAll(true)} disabled={refreshing} title="Refresh"
              className="p-1.5 rounded-lg text-gray-400 hover:text-[#00D4BE] hover:bg-[#00D4BE]/10 transition-colors disabled:opacity-40">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            {/* Exports `filtered` — the same array the list renders, so the file
                carries the active hierarchy filter, status pill, search and sort. */}
            <button type="button" onClick={() => exportDevicesCsv(filtered, metrics)} disabled={filtered.length === 0}
              title={filtered.length ? `Export ${filtered.length} listed device${filtered.length === 1 ? '' : 's'} to CSV` : 'Nothing to export'}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:text-[#00D4BE] hover:border-[#00D4BE]/50 transition-colors disabled:opacity-40 disabled:hover:text-gray-600 disabled:hover:border-gray-200">
              <Download className="w-4 h-4" />
              Export
            </button>
            {/* Sorts the same `filtered` array the list renders, so the CSV
                export inherits the order without any extra plumbing. */}
            <div className="flex items-center">
              {/* Last seen reads off the device row, so it stays available even
                  if /setup/device-metrics failed; only the metric sorts wait. */}
              <select value={sortField}
                onChange={e => setSortField(e.target.value as SortField)}
                title="Sort the listed devices"
                className={`px-3 py-2 text-sm bg-white border border-gray-200 text-gray-600
                  focus:outline-none focus:border-[#00D4BE]/60 transition-colors
                  ${effectiveSortField === 'default' ? 'rounded-lg' : 'rounded-l-lg border-r-0'}`}>
                <option value="default">Sort: {SORT_LABEL.default}</option>
                <option value="lastseen">Sort: {SORT_LABEL.lastseen}</option>
                <option value="drain"     disabled={!metricsLoaded}>Sort: {SORT_LABEL.drain}</option>
                <option value="resets"    disabled={!metricsLoaded}>Sort: {SORT_LABEL.resets}</option>
                <option value="scheduled" disabled={!metricsLoaded}>Sort: {SORT_LABEL.scheduled}</option>
              </select>
              {effectiveSortField !== 'default' && (
                <button type="button"
                  // While the sort is automatic there is no explicit field to flip,
                  // so the first click materialises it as a real choice and reverses
                  // the direction in one step.
                  onClick={() => {
                    if (sortIsAutomatic) { setSortField('lastseen'); setSortDir('asc'); }
                    else setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
                  }}
                  title={`Currently ${SORT_DIR_HINT[effectiveSortField][effectiveSortDir]} — click to reverse`}
                  className="flex items-center gap-1 px-2.5 py-2 text-xs text-gray-500 bg-white border border-gray-200 rounded-r-lg hover:text-[#00D4BE] hover:border-[#00D4BE]/50 transition-colors">
                  {effectiveSortDir === 'desc' ? <ArrowDown className="w-3.5 h-3.5" /> : <ArrowUp className="w-3.5 h-3.5" />}
                  {SORT_DIR_HINT[effectiveSortField][effectiveSortDir]}
                </button>
              )}
            </div>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by code, name, machine…"
              className="w-full sm:w-64 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D4BE]/60 transition-colors"
            />
          </div>
        </div>

        {loading && <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Loading devices…</div>}
        {error   && <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
        {refreshStale && !error && (
          <div className="text-amber-700 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Auto-refresh failed — showing the last successful load; data may be stale.
          </div>
        )}

        {!loading && !error && (
          <>
            <StatusBar devices={visibleForStatusBar} statusFilter={statusFilter} onStatusFilter={setStatusFilter} />
            {/* The fleet baseline is ONE shared value across every database, not a
                per-row computation — state it so "x fleet" on a card is readable. */}
            {fleet?.mean_drain_mv_per_day !== null && fleet !== null && (
              <div className="text-xs text-gray-400 mb-3">
                Fleet mean battery drain{' '}
                <span className="text-gray-600 font-medium">
                  {fleet.mean_drain_mv_per_day!.toFixed(2)} mV/day
                </span>{' '}
                — mean across {fleet.n_devices} devices in all databases with a confident{' '}
                {fleet.window_days}-day fit. Flagged at ≥{fleet.outlier_ratio}× the mean;
                battery resets counted over {fleet.reset_window_days} days, flagged at ≥
                {fleet.reset_frequent_min}.
              </div>
            )}
            {effectiveSortField !== 'default' && (
              <div className="text-xs text-gray-400 mb-3">
                Sorted by <span className="text-gray-600 font-medium">{SORT_LABEL[effectiveSortField].toLowerCase()}</span>
                {' — '}{SORT_DIR_HINT[effectiveSortField][effectiveSortDir]}
                {/* Say so when the page chose the sort, not the user. */}
                {sortIsAutomatic && ', applied automatically for the disconnected filter'}.
                {unsortableCount > 0 && (
                  <> <span className="text-gray-500">
                    {unsortableCount} device{unsortableCount === 1 ? '' : 's'} with no timestamp listed last
                  </span> (not the same as a recent one).</>
                )}
              </div>
            )}
            {metricsFailed && (
              <div className="text-amber-700 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                Battery-drain and collection metrics could not be loaded — those columns show “—”.
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="flex items-center justify-center h-32 text-gray-400 text-sm">No devices match the current filter.</div>
              )}
              {filtered.map(d => (
                <DeviceCard key={`${d.phantom_code}-${d.point_index}`} device={d}
                  onEditAxis={() => setEditingDevice(d)}
                  onChangeSensor={() => setChangingSensor(d)}
                  onDelete={() => setDeletingDevice(d)}
                  onOpenPoint={onPointClick
                    ? () => onPointClick(d.machine_code, d.machine_name, d.point_name, d.point_index)
                    : undefined}
                  metrics={metrics[d.phantom_code]}
                  fleet={fleet}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
